import { useMemo, useState, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import Breadcrumb from "@components/UI/Breadcrumb";
import WorkflowProgressVerificationReport from "@components/UI/WorkflowProgressVerificationReport";
import { ComponentTable, ComponentTableHeader } from "@components/Tables";
import { WriteIcon, ReviewIcon, DistributedIcon } from "@components/Icons";
import useVerificationReportDetail from "@hooks/useVerificationReportDetail";
import { PROGRESS_VERIFICATION_REPORT_STATUS } from "@constants/verificationReport";
import { VEX_SNAPSHOT_SOURCE } from "@constants/vex";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import { formatVerificationType } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import OutlineButton from "@components/Buttons/OutlineButton";
import { PrimaryButton } from "@components/Buttons";
import { useMutationData } from "@hooks/useMutationData";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { CustomInput } from "@components/Forms";
import { PermissionContext } from "@contexts/PermissionContext";

const VerificationReportDetail = () => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { verificationReportSeq } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    verificationReportName: ""
  });
  const {
    fetchedVerificationData,
    verificationReportStatus,
    scanComponents,
    fetchedLicensePolicy,
    isLoading,
    handleMoveToDistribute,
    handleMoveToReview,
    handleRejectReReview,
    handleRejectRequestReview,
    refetchVerificationData,
  } = useVerificationReportDetail(verificationReportSeq);
  const { hasEditPermission } = useRoutePermissions();
  const { canManageProjectWorkflow } = useContext(PermissionContext);

  const projectManagerId = fetchedVerificationData?.projectManagerId;
  const canEditVerificationReport =
    hasEditPermission && canManageProjectWorkflow(projectManagerId);

  useEffect(() => {
    if (!canEditVerificationReport && isEditMode) {
      setIsEditMode(false);
    }
  }, [canEditVerificationReport, isEditMode]);

  const breadcrumbItems = [
    { label: t("project.verification-report.verification-report-list"), href: "/verification-report" },
    { label: t("project.verification-report.verification-report-details") },
  ];

  const workflowSteps = [
    { label: "Write", icon: WriteIcon, stepName: "write" },
    { label: "Review", icon: ReviewIcon, stepName: "review" },
    { label: "Distributed", icon: DistributedIcon, stepName: "distributed" },
  ];

  const getCurrentStepIndex = () => {
    const statusStepMap = {
      [PROGRESS_VERIFICATION_REPORT_STATUS.WRITE]: 0,
      [PROGRESS_VERIFICATION_REPORT_STATUS.REVIEW]: 1,
      [PROGRESS_VERIFICATION_REPORT_STATUS.DISTRIBUTION_DONE]: 2,
    };
    return statusStepMap[verificationReportStatus] || 0;
  };

  const updateVerificationReportData = useMutationData(
    `/verification_report_name`,
    { method: "PUT" },
    {
      onSuccess: () => {
        showSuccessToast(t("project.verification-report.update-success"));
        setIsEditMode(false);
        refetchVerificationData();
      },
      onError: (error) => {
        console.error("Error updating verification report data:", error);
        showErrorToast(t("project.verification-report.update-error"));
      },
    },
  );

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({
      verificationReportName: fetchedVerificationData?.verificationReportName || ""
    });
  };

  const handleSaveEdit = () => {
    if (!editFormData.verificationReportName?.trim()) {
      showErrorToast(t("validation.required-field"), "");
      return;
    }

    updateVerificationReportData.mutate({
      verificationReportSeq: verificationReportSeq,
      verificationReportName: editFormData.verificationReportName.trim()
    });
  };

  const handleEditClick = () => {
    setEditFormData({
      verificationReportName: fetchedVerificationData?.verificationReportName || ""
    });
    setIsEditMode(true);
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const VerificationReportInfoSection = useMemo(() => {
    if (!fetchedVerificationData) {
      return <></>
    }
    return (
      <div className={`${colors.bgBasicInfo} mx-12 p-4 rounded-t-2xl min-w-[832px]`} data-testid="verification-report-detail-basic-info">
        <div className="flex items-center justify-between pb-4 pt-2" data-testid="verification-report-detail-basic-info-grid">
          <h3 className={`text-lg font-bold ${colors.textPrimary}`}>
            {t("project.verification-report.sbom-report-information")}
          </h3>
          {canEditVerificationReport && (
            isEditMode ? (
            <div className="flex space-x-3">
              <OutlineButton onClick={handleCancelEdit}>{t("common.cancel-button")}</OutlineButton>
              <PrimaryButton
                onClick={handleSaveEdit}
                disabled={
                  updateVerificationReportData.isPending ||
                  !editFormData.verificationReportName?.trim() ||
                  editFormData.verificationReportName?.trim() === fetchedVerificationData?.verificationReportName?.trim()
                }
              >
                {updateVerificationReportData.isPending ? t("common.saving") : t("common.save-button")}
              </PrimaryButton>
            </div>
          ) : (
            <OutlineButton onClick={handleEditClick}>
              {t("project.imported-components.edit-information")}
            </OutlineButton>
          ))}
          
        </div>
        <div className="space-y-4">
          {/* Row 1 */}
          <div className="grid grid-cols-3 gap-2 2xl:grid-cols-4 overflow-hidden" data-testid="verification-report-detail-field-report-name">
            <div className="flex items-center min-w-0">
              <label style={{ fontSize: "0.8125rem" }} className={`w-32 font-normal ${colors.textSubTitle} flex-shrink-0`}>
                {t("project.verification-report.verif-report-name")}
              </label>
              {isEditMode ? (
                <CustomInput
                  type="text"
                  value={editFormData.verificationReportName}
                  onChange={(e) => handleInputChange('verificationReportName', e.target.value)}
                  placeholder={t("project.verification-report.verif-report-name")}
                  size="sm"
                  maxLength={100}
                />
              ) : (
                <p className={`ml-1 text-sm font-medium ${colors.textPrimary} truncate flex-1 min-w-0`}
                  title={fetchedVerificationData.verificationReportName || "---"}>
                  {fetchedVerificationData.verificationReportName || "-"}
                </p>
              )}
            </div>
            <div className="flex items-center min-w-0" data-testid="verification-report-detail-field-verification-version">
              <label style={{ fontSize: "0.8125rem" }} className={`w-32 font-normal ${colors.textSubTitle} flex-shrink-0`}>
                {t("status.verification-version")}
              </label>
              <p className={`ml-1 text-sm font-medium ${colors.textPrimary} truncate flex-1 min-w-0`}
                title={fetchedVerificationData.verificationVersion || "-"}>
                {fetchedVerificationData.verificationVersion}
              </p>
            </div>
            <div className="flex items-center min-w-0" data-testid="verification-report-detail-field-verif-file-name">
              <label style={{ fontSize: "0.8125rem" }} className={`w-32 font-normal ${colors.textSubTitle} flex-shrink-0`}>
                {t("project.verification-report.verif-file-name")}
              </label>
              <p
                title={fetchedVerificationData.reportFileName || "-"}
                className={`ml-1 text-sm font-medium ${colors.textPrimary} truncate flex-1 min-w-0`}
              >
                {fetchedVerificationData.reportFileName || "-"}
              </p>
            </div>
            <div className="flex items-center min-w-0" data-testid="verification-report-detail-field-verif-report-creator">
              <label style={{ fontSize: "0.8125rem" }} className={`w-32 font-normal ${colors.textSubTitle} flex-shrink-0`}>
                {t("project.verification-report.verif-report-creator")}
              </label>
              <div className="ml-1 flex-1 min-w-0">
                <UserNameWithId name={fetchedVerificationData.createrName} id={fetchedVerificationData.createrId} layout="inline" />
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className={`border-t ${colors.borderPrimary}`} />

          {/* Row 2 */}
          <div className="grid grid-cols-3 gap-2 2xl:grid-cols-4 overflow-hidden" data-testid="verification-report-detail-field-verif-report-id">
            <div className="flex items-center min-w-0">
              <label style={{ fontSize: "0.8125rem" }} className={`w-32 font-normal ${colors.textSubTitle} flex-shrink-0`}>
                {t("project.verification-report.verif-report-id")}
              </label>
              <p className={`ml-1 text-sm font-medium ${colors.textPrimary} truncate flex-1 min-w-0`}
                title={fetchedVerificationData.verificationReportId || "-"}>
                {fetchedVerificationData.verificationReportId || "-"}
              </p>
            </div>
            <div className="flex items-center min-w-0" data-testid="verification-report-detail-field-verif-type">
              <label style={{ fontSize: "0.8125rem" }} className={`w-32 font-normal ${colors.textSubTitle} flex-shrink-0`}>
                {t("project.verification-report.verif-type")}
              </label>
              <p className={`ml-1 text-sm font-medium ${colors.textPrimary} truncate flex-1 min-w-0`}
                title={formatVerificationType(fetchedVerificationData.verificationToolCdName) || "-"}>
                {formatVerificationType(fetchedVerificationData.verificationToolCdName)}
              </p>
            </div>
            <div className="flex items-center min-w-0"  data-testid="verification-report-detail-field-project-id">
              <label style={{ fontSize: "0.8125rem" }} className={`w-32 font-normal ${colors.textSubTitle} flex-shrink-0`}>
                {t("project.verification-report.project-id")}
              </label>
              <p className={`ml-1 text-sm font-medium ${colors.textPrimary} truncate flex-1 min-w-0`}
                title={fetchedVerificationData.projectId || "-"}>
                {fetchedVerificationData.projectId || "-"}
              </p>
            </div>
            {/* <div className="flex items-center min-w-0" data-testid="verification-report-detail-field-creator-department">
              <label style={{ fontSize: "0.8125rem" }} className={`w-32 font-normal ${colors.textSubTitle} flex-shrink-0`}>
                {t("project.verification-report.creator-department")}
              </label>
              <p className={`ml-1 text-sm font-medium ${colors.textPrimary} truncate flex-1 min-w-0`}
                title={translateDepartment(t, fetchedVerificationData.createrDeptCdName) || "-"}>
                {translateDepartment(t, fetchedVerificationData.createrDeptCdName) || "-"}
              </p>
            </div> */}
          </div>
        </div>
      </div>
    )
  }, [fetchedVerificationData, colors, t, isEditMode, editFormData, updateVerificationReportData.isPending, canEditVerificationReport])

  if (isLoading) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div className={`min-h-full ${colors.bgSecondary}`} data-testid="verification-report-detail-page">
      {/* Header */}
      <div className="pb-5" data-testid="verification-report-detail-header">
        <div className="space-y-3">
          <Breadcrumb data-testid="verification-report-detail-breadcrumb" items={breadcrumbItems} />
          <div data-testid="verification-report-detail-title">
            <h1 className={`text-2xl font-bold ${colors.textPrimary} truncate`}>
              {fetchedVerificationData?.verificationReportName || ""}
            </h1>
          </div>
        </div>
      </div>

      <div data-testid="verification-report-detail-body">
        {/* Basic Information Section */}
        {VerificationReportInfoSection}

        {/* Workflow Progress Section */}
        <div className={`px-6 py-4 rounded-2xl bg-[${colors.bgPrimaryColor}]`} data-testid="verification-report-detail-workflow-section">
          <WorkflowProgressVerificationReport
            dataTestId="verification-report-detail-workflow"
            verificationReportSeq={verificationReportSeq}
            projectId={fetchedVerificationData?.projectId}
            steps={workflowSteps}
            currentStep={getCurrentStepIndex()}
            projectManagerId={projectManagerId}
            onRequestReview={handleMoveToReview}
            onRejectReview={handleRejectRequestReview}
            onDistribute={handleMoveToDistribute}
            onReReview={handleRejectReReview}
          />
        </div>

        {/* OSS Usage Information Section */}
        <div className="mt-6" data-testid="verification-report-detail-oss-section">
          <div className={`mb-4 rounded-2xl p-6 ${colors.bgPrimary} ${colors.borderPrimary}`} data-testid="verification-report-detail-table">
            <div className="mb-4" data-testid="verification-report-detail-table-header">
              <ComponentTableHeader
                title={t("project.verification-report.ossused-info")}
                verificationReportStatus={verificationReportStatus}
                count={scanComponents?.length || 0}
                isEditMode={false}
                isEditingAllowed={false}
                colors={colors}
                t={t}
                verificationReportSeq={verificationReportSeq}
                fetchedVerificationData={fetchedVerificationData}
                showVexProgress
                frozenVexSnapshot={
                  verificationReportSeq
                    ? {
                        source: VEX_SNAPSHOT_SOURCE.VERIFICATION_REPORT,
                        verificationReportSeq: Number(verificationReportSeq),
                      }
                    : null
                }
              />
            </div>
            <ComponentTable
              data={scanComponents}
              licensePolicyData={fetchedLicensePolicy}
              seq="verificationReportDetailSeq"
              validationMode="export"
              showShieldFilter
              editableFrozenVexSnapshot={canEditVerificationReport}
              frozenVexSnapshot={
                verificationReportSeq
                  ? {
                      source: VEX_SNAPSHOT_SOURCE.VERIFICATION_REPORT,
                      verificationReportSeq: Number(verificationReportSeq),
                    }
                  : null
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationReportDetail;
